import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Avatar,
  Rating,
  Divider,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  TuneRounded as FilterIcon,
  LocationOn as LocationIcon,
  Build as ServiceIcon,
  Euro as PriceIcon,
  Speed as UrgencyIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  Psychology as AIIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const SmartMatching = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [budget, setBudget] = useState([0, 5000]);
  const [urgency, setUrgency] = useState('standard');
  const [preferences, setPreferences] = useState({
    experience: 5,
    rating: 4,
    availability: 'any',
    certifications: false
  });
  const [matchResults, setMatchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchAnalysis, setSearchAnalysis] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Types de services disponibles
  const serviceTypes = [
    'Plomberie',
    'Électricité',
    'Menuiserie',
    'Peinture',
    'Maçonnerie',
    'Jardinage',
    'Rénovation',
    'Isolation',
    'Chauffage',
    'Climatisation'
  ];

  // Villes disponibles (simulation)
  const cities = [
    'Paris',
    'Lyon',
    'Marseille',
    'Toulouse',
    'Nice',
    'Nantes',
    'Strasbourg',
    'Montpellier',
    'Bordeaux',
    'Lille'
  ];

  // Recherche intelligente
  const handleSmartSearch = async () => {
    if (!searchQuery.trim() && !serviceType) {
      return;
    }

    setIsLoading(true);
    try {
      // Si c'est une recherche par texte libre, utiliser la recherche intelligente
      if (searchQuery.trim()) {
        const response = await fetch('/api/ai/smart-search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: searchQuery,
            location: location ? { codePostal: location } : null,
            filters: {
              budget,
              urgency,
              preferences
            }
          })
        });

        const data = await response.json();
        if (data.success) {
          setMatchResults(data.data.results);
          setSearchAnalysis(data.data.searchAnalysis);
        }
      } else {
        // Sinon, utiliser le matching traditionnel
        const response = await fetch('/api/ai/match-artisan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            serviceType,
            location: { codePostal: location },
            budget: { min: budget[0], max: budget[1] },
            urgency,
            preferences
          })
        });

        const data = await response.json();
        if (data.success) {
          setMatchResults(data.data);
        }
      }
    } catch (error) {
      console.error('Erreur recherche intelligente:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Composant Artisan Card avec score IA
  const ArtisanCard = ({ artisan, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card 
        sx={{ 
          mb: 2, 
          position: 'relative',
          border: artisan.matchScore > 0.8 ? '2px solid #4caf50' : '1px solid #e0e0e0',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)'
          },
          transition: 'all 0.3s ease'
        }}
      >
        {/* Badge de match parfait */}
        {artisan.matchScore > 0.9 && (
          <Chip
            icon={<MagicIcon />}
            label="Match Parfait"
            color="success"
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1
            }}
          />
        )}

        <CardContent>
          <Grid container spacing={2}>
            {/* Informations principales */}
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  src={artisan.photo}
                  sx={{ width: 60, height: 60 }}
                >
                  {artisan.nom?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{artisan.nom} {artisan.prenom}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {artisan.typeMetier}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Rating value={artisan.noteGlobale || 4.5} readOnly size="small" />
                    <Typography variant="caption">
                      ({artisan.nbAvis || 12} avis)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Raisons du match (IA) */}
              {artisan.matchReasons && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    <AIIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Pourquoi ce match ?
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {artisan.matchReasons.map((reason, idx) => (
                      <Chip
                        key={idx}
                        label={reason}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Localisation et disponibilité */}
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {artisan.adresse?.ville || 'Paris'} ({artisan.distance || '2.3'} km)
                  </Typography>
                </Box>
                <Chip
                  label={artisan.disponibilite || 'Disponible'}
                  color={artisan.disponibilite === 'Disponible' ? 'success' : 'warning'}
                  size="small"
                />
              </Box>

              {/* Spécialités */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Spécialités:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {(artisan.specialites || ['Installation', 'Dépannage', 'Maintenance']).map((spec, idx) => (
                    <Chip
                      key={idx}
                      label={spec}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Score et actions */}
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                {/* Score de match IA */}
                <Box mb={2}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    {Math.round((artisan.matchScore || artisan.aiScore || 0.8) * 100)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Compatibilité IA
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(artisan.matchScore || artisan.aiScore || 0.8) * 100}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                </Box>

                {/* Prix estimé */}
                <Box mb={2}>
                  <Typography variant="h6" color="success.main">
                    {artisan.prixEstime || '€€€'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Prix moyen
                  </Typography>
                </Box>

                {/* Actions */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    startIcon={<StarIcon />}
                  >
                    Contacter
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                  >
                    Voir Profil
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <AIIcon color="primary" />
          <Box>
            <Typography variant="h5">Matching Intelligent IA</Typography>
            <Typography variant="body2" color="text.secondary">
              Trouvez l'artisan parfait grâce à l'intelligence artificielle
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Barre de recherche intelligente */}
        <Box mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Décrivez votre besoin: 'Fuite urgente cuisine' ou 'Rénovation salle de bain'..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ mb: { xs: 2, md: 0 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                value={location}
                onChange={(event, newValue) => setLocation(newValue)}
                options={cities}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Ville"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleSmartSearch}
                  disabled={isLoading}
                  startIcon={<MagicIcon />}
                  fullWidth
                >
                  {isLoading ? 'Recherche...' : 'Recherche IA'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowFilters(!showFilters)}
                  startIcon={<FilterIcon />}
                >
                  Filtres
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Filtres avancés */}
          <Accordion expanded={showFilters} onChange={() => setShowFilters(!showFilters)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Filtres Avancés</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de Service</InputLabel>
                    <Select
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                      label="Type de Service"
                    >
                      {serviceTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Urgence</InputLabel>
                    <Select
                      value={urgency}
                      onChange={(e) => setUrgency(e.target.value)}
                      label="Urgence"
                    >
                      <MenuItem value="low">Pas urgent</MenuItem>
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="high">Urgent</MenuItem>
                      <MenuItem value="emergency">Urgence</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom>Budget (€)</Typography>
                  <Slider
                    value={budget}
                    onChange={(e, newValue) => setBudget(newValue)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={10000}
                    step={100}
                    marks={[
                      { value: 0, label: '0€' },
                      { value: 2500, label: '2500€' },
                      { value: 5000, label: '5000€' },
                      { value: 10000, label: '10000€' }
                    ]}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Analyse de recherche IA */}
        {searchAnalysis && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">Analyse IA de votre recherche:</Typography>
            <Typography variant="body2">
              Service détecté: <strong>{searchAnalysis.serviceType}</strong> • 
              Urgence: <strong>{searchAnalysis.urgency}</strong> • 
              Complexité: <strong>{searchAnalysis.complexity}</strong>
            </Typography>
          </Alert>
        )}

        {/* Chargement */}
        {isLoading && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" gutterBottom>
              🤖 L'IA analyse vos critères...
            </Typography>
            <LinearProgress sx={{ mt: 2, mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Recherche des artisans les plus compatibles
            </Typography>
          </Box>
        )}

        {/* Résultats du matching */}
        {matchResults.length > 0 && !isLoading && (
          <Box>
            <Typography variant="h6" gutterBottom>
              <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {matchResults.length} artisan{matchResults.length > 1 ? 's' : ''} trouvé{matchResults.length > 1 ? 's' : ''} par l'IA
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <AnimatePresence>
              {matchResults.map((artisan, index) => (
                <ArtisanCard
                  key={artisan.id || index}
                  artisan={artisan}
                  index={index}
                />
              ))}
            </AnimatePresence>
          </Box>
        )}

        {/* Aucun résultat */}
        {matchResults.length === 0 && !isLoading && searchQuery && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              Aucun artisan trouvé
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Essayez d'élargir vos critères de recherche
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartMatching;